// src/components/common/MessageConfirmationModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { WSButton } from '../Templates';
import { FaRegPaperPlane } from 'react-icons/fa';
import './MessageConfirmationModal.css';

/**
 * Confirmation modal for starting new conversations
 * Helps prevent accidental conversation starts
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to call when modal is closed
 * @param {function} props.onConfirm - Function to call when user confirms
 * @param {Object} props.post - Post object containing user and post details
 * @param {boolean} props.isLoading - Whether the confirmation action is in progress
 */
const MessageConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  post,
  isLoading = false
}) => {
  const { t } = useTranslation(['common', 'chat']);

  if (!post) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  // Get post type for display
  const getPostType = () => {
    if (post.isPackage) {
      return t('posts.package');
    }
    return post.role === 'Driver' ? t('posts.driver') : t('posts.passenger');
  };

  // Get route display
  const getRouteDisplay = () => {
    const from = post.address_from_city || post.address_from_street || t('posts.not_specified');
    const to = post.address_to_city || post.address_to_street || t('posts.not_specified');
    return `${from} → ${to}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('chat.start_conversation')}
      modalSize="small"
      closeOnEscape={!isLoading}
      closeOnOverlayClick={!isLoading}
    >
      <div className="message-confirmation-content">
        {/* Start-conversation icon (friendly — this isn't a destructive action) */}
        <div className="confirmation-icon">
          <FaRegPaperPlane />
        </div>

        {/* Confirmation Message */}
        <div className="confirmation-text">
          <p className="main-message">
            {t('chat.confirm_start_conversation')}
          </p>
          <p className="sub-message">
            {t('chat.new_conversation_warning')}
          </p>
        </div>

        {/* Post Details */}
        <div className="post-preview">
          <div className="post-preview-header">
            <img
              src={post.profiles?.avatar_url || '/default-avatar.png'}
              alt={post.profiles?.username || t('chat.unknown_user')}
              className="preview-avatar"
            />
            <div className="preview-user-info">
              <h4 className="preview-username">
                {post.profiles?.username || t('chat.unknown_user')}
              </h4>
              <span className={`preview-post-type role-${post.isPackage ? 'package' : post.role === 'Driver' ? 'driver' : 'passenger'}`}>
                {getPostType()}
              </span>
            </div>
          </div>
          
          <div className="post-preview-details">
            <div className="preview-route">
              <span className="preview-label">{t('posts.route')}:</span>
              <span className="preview-value">{getRouteDisplay()}</span>
            </div>
            <div className="preview-date">
              <span className="preview-label">{t('posts.date')}:</span>
              <span className="preview-value">
                {/* For recurring posts, show selected occurrence date; otherwise show travel_date */}
                {post.selectedOccurrenceDate ?
                  new Date(post.selectedOccurrenceDate).toLocaleDateString('en-GB') :
                  (post.travel_date ?
                    new Date(post.travel_date).toLocaleDateString('en-GB') :
                    t('posts.not_specified'))
                }
              </span>
            </div>
            {/* Show time if available (either selected occurrence time or travel time) */}
            {(post.selectedOccurrenceTime || post.travel_time) && (
              <div className="preview-time">
                <span className="preview-label">{t('posts.time')}:</span>
                <span className="preview-value">
                  {/* Remove seconds from time display (HH:MM:SS -> HH:MM) */}
                  {(post.selectedOccurrenceTime || post.travel_time).substring(0, 5)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="confirmation-actions">
          <WSButton
            variant="secondary"
            label={t('buttons.cancel')}
            onClick={handleCancel}
            disabled={isLoading}
          />
          <WSButton
            variant="primary"
            label={isLoading ? t('chat.starting') : t('chat.yes_start_conversation')}
            onClick={handleConfirm}
            disabled={isLoading}
            icon={<FaRegPaperPlane />}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MessageConfirmationModal;
